import _ from "lodash/fp";
import JSZip from "jszip";
import { v4 as uuidv4 } from "uuid";

import {
  doItemNamesExist,
  doPassageNamesExist,
  getItemsByName,
  getPassagesByName,
  removeTypenameFromObject,
} from "../BatchDataUpload/bdu.js";
import { cleanContent } from "./content.js";
import {
  getCharacterCount,
  removeClassMsoNormalAttr,
} from "../../../services/contentUtils";

const CHUNKSIZE = 25;
const MANIFEST_FILE_NAME = "imsmanifest.xml";
const MISSING_MANIFEST_MESSAGE = `${MANIFEST_FILE_NAME} was not found in the .zip file, so it is not a QTI package`;

const IMPORT_RESULT_STATUS_DETAILS = {
  Valid: "New content will be created.",
  "Valid - Overwrite": "Existing content with this name will be overwritten.",
  Invalid: "This name is already used by different content in another program.",
};

const getImportResultDetails = (status) =>
  IMPORT_RESULT_STATUS_DETAILS[status] ?? "";

const INCLUSION_ORDER_TYPES = {
  "apip:brailleDefaultOrder": "Braille : Default",
  "apip:graphicsOnlyOnDemandOrder": "Spoken, Graphics Only : On Demand",
  "apip:nonVisualDefaultOrder": "Spoken, Non-visual : Default",
  "apip:textGraphicsDefaultOrder": "Spoken, Text and Graphics : Default",
  "apip:textGraphicsOnDemandOrder": "Spoken, Text and Graphics : On Demand",
  "apip:textOnlyDefaultOrder": "Spoken, Text Only : Default",
  "apip:textOnlyOnDemandOrder": "Spoken, Text Only : On Demand",
};

const MIME_TYPES = {
  gif: "image/gif",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  svg: "image/svg+xml",
};

const OPTIONAL_METADATA_FIELDS = [
  "contentArea",
  "contentSpecialist",
  "developmentState",
  "itemWriter",
];

const withOptionalMetadata = (metadata) => (obj) => ({
  ...obj,
  ..._.pickBy(_.identity)(_.pick(OPTIONAL_METADATA_FIELDS)(metadata)),
});

const createContentForImport =
  (client) =>
  async ({
    contentArea,
    contentSpecialist,
    developmentState,
    itemWriter,
    items,
    passages,
    program,
  }) => {
    if (!items.length && !passages.length)
      return { itemsCreated: [], passagesCreated: [] };
    const buildContentShell = ({ name }) =>
      withOptionalMetadata({
        contentArea,
        contentSpecialist,
        developmentState,
        itemWriter,
      })({ name });
    return client
      .mutation(
        getCreateContentForImportMutation,
        {
          items: items.filter(({ id }) => !id).map(buildContentShell),
          passages: passages.filter(({ id }) => !id).map(buildContentShell),
          program,
        },
        { requestPolicy: "network-only" },
      )
      .toPromise()
      .then((response) => {
        if (response.error) throw new Error(response.error);
        return response.data?.createContentForImport;
      });
  };

const extractFiles = async (file) => {
  const zip = new JSZip();
  const result = await zip.loadAsync(file);
  console.info("Files extracted from zip:", result);
  return result.files;
};

const generateAccessibilityElements = (xmlDocument) => (content) => {
  const parentAccessibilityElement = xmlDocument
    .getElementsByTagName("apip:apipAccessibility")
    .item(0);
  const accessibilityInfoElement = parentAccessibilityElement
    ?.getElementsByTagName("apip:accessibilityInfo")
    .item(0);
  const inclusionOrderElement = parentAccessibilityElement
    ?.getElementsByTagName("apip:inclusionOrder")
    .item(0);
  if (
    !parentAccessibilityElement ||
    !accessibilityInfoElement ||
    !inclusionOrderElement
  )
    return [];

  // Get the inclusionOrders for each accessibility type
  const inclusionOrders = Object.keys(INCLUSION_ORDER_TYPES).reduce(
    (acc, tagName) => {
      const childElement = inclusionOrderElement
        ?.getElementsByTagName(tagName)
        ?.item(0);
      if (!childElement) return { ...acc };

      const order = _.pipe([
        _.sortBy((el) => parseInt(el.textContent)),
        _.map((el) => el.getAttribute("identifierRef")),
      ])([...childElement.getElementsByTagName("apip:elementOrder")]);
      return {
        ...acc,
        [INCLUSION_ORDER_TYPES[tagName]]: order,
      };
    },
    {},
  );

  const accessibilityElements = [
    ...accessibilityInfoElement.getElementsByTagName("apip:accessElement"),
  ]
    .map((accessElement) => {
      const contentLinkInfoElement = accessElement
        .getElementsByTagName("apip:contentLinkInfo")
        .item(0);
      const identifier = contentLinkInfoElement.getAttribute(
        "qtiLinkIdentifierRef",
      );
      const contentElement = content.querySelector(`[id="${identifier}"]`);
      if (!contentElement) return null;

      const span = xmlDocument.createElement("span");
      span.setAttribute("id", identifier);
      span.innerHTML = contentElement.innerHTML;
      contentElement.replaceWith(span);

      const brailleFeatures = generateBrailleFeatures(accessElement) || [];

      const pronunciationFeatures =
        generatePronunciationFeatures(identifier)(accessElement) || [];

      const spokenFeatures = generateSpokenFeatures(accessElement) || [];

      const translationFeatures =
        generateTranslationFeatures(accessElement) || [];

      const accessibilityFeatures = [
        ...brailleFeatures,
        ...pronunciationFeatures,
        ...spokenFeatures,
        ...translationFeatures,
      ];

      return {
        accessibilityFeatures,
        identifier,
        inclusionOrders: Object.keys(inclusionOrders).filter((type) =>
          inclusionOrders[type].includes(
            accessElement.getAttribute("identifier"),
          ),
        ),
      };
    })
    .filter(Boolean);

  const ids = [...content.querySelectorAll("[id]")];
  return _.sortBy(({ identifier }) => ids.indexOf(identifier))(
    accessibilityElements,
  );
};

const generateBrailleFeatures = (accessElement) =>
  [...accessElement.getElementsByTagName("apip:brailleTextString")]
    .map((element) => {
      const text = element.textContent.trim();
      if (!text) return null;
      return {
        feature: "Braille Text",
        type: "Braille",
        text,
      };
    })
    .filter(Boolean);

const generateChoiceInteractions =
  ({ accessibilityElements = [] }) =>
  (xmlDocument) =>
  (element) => {
    const identifier = element.getAttribute("responseIdentifier");
    const correctResponses =
      [
        ...xmlDocument.querySelectorAll(
          `responseDeclaration[identifier="${identifier}"] > correctResponse > value`,
        ),
      ]?.map((el) => el.textContent) || [];
    const foils = [...element.querySelectorAll("simpleChoice")].map((el) => {
      const feedbackBlock = el.querySelector("feedbackBlock");
      const distractorRationale = feedbackBlock?.innerHTML || "";
      feedbackBlock?.remove();
      return {
        content: cleanContent({ accessibilityElements })(el.innerHTML),
        correct: correctResponses.includes(el.getAttribute("identifier")),
        distractorRationale,
      };
    });
    const maxChoices = element.getAttribute("maxChoices") || 1;
    const minChoices = element.getAttribute("minChoices") || 0;
    const section = xmlDocument.createElement("section");
    section.classList.add("interaction");
    section.innerHTML = `Selected Response (${identifier})`;
    section.setAttribute("data-interaction-name", identifier);
    section.setAttribute("data-interaction-type", "Choice");
    element.replaceWith(section);
    return {
      foils,
      identifier,
      maxChoices: parseInt(maxChoices),
      minChoices: parseInt(minChoices),
    };
  };

const generateExtendedTextInteractions = (xmlDocument) => (element) => {
  const identifier = element.getAttribute("responseIdentifier");
  const section = xmlDocument.createElement("section");
  section.classList.add("interaction");
  section.innerHTML = `Constructed Response (${identifier})`;
  section.setAttribute("data-interaction-name", identifier);
  section.setAttribute("data-interaction-type", "Extended Text");
  element.replaceWith(section);
  return {
    expectedLength: element.getAttribute("expectedLength"),
    expectedLines: element.getAttribute("expectedLines"),
    identifier,
    scoringCriteria: element.getAttribute("scoringCriteria"),
  };
};

const generateImageAssets =
  ({ fileNames, fileTimestamp, id }) =>
  (element) => {
    const altText = element.getAttribute("alt");
    const src = element.getAttribute("src");
    const name = getImageFilename(fileNames)(src);
    const printableAsset = getPrintableAsset({ fileNames, fileTimestamp })(
      name,
    );
    element.setAttribute("src", `/assets/${id}/${name}`);
    return {
      altText,
      fileTimestamp,
      name,
      printableAsset,
    };
  };

const generateItems =
  ({ createdObjects, fileNames, fileTimestamp }) =>
  (item) => {
    const id =
      item.id ||
      createdObjects.itemsCreated.find((o) => o.name === item.name)?.id;
    const xmlDocument = getXMLContent(item.qtiXml);

    // Determine the following prior to content being altered
    const snapToHighlight = [
      ...xmlDocument.querySelectorAll(
        "choiceInteraction,extendedTextInteraction",
      ),
    ].some((element) => element.classList.contains("behavior-highlightscroll"));

    // Get image assets before removing interaction elements
    const imageAssets = [...xmlDocument.querySelectorAll("img")].map(
      generateImageAssets({ fileNames, fileTimestamp, id }),
    );

    // Generate accessibility elements
    const accessibilityElements = generateAccessibilityElements(xmlDocument)(
      xmlDocument.querySelector("itemBody"),
    );

    // Determine what to do with interactions
    const unsupportedInteractionElements = [
      ...xmlDocument.querySelectorAll("*"),
    ].filter(
      (element) =>
        element?.tagName.endsWith("Interaction") &&
        !["choiceInteraction", "extendedTextInteraction"].includes(
          element?.tagName,
        ),
    );

    // Figure out whether this needs to be a passthrough item
    const passthrough = !!unsupportedInteractionElements.length;

    // Get interactions from content (and update content accordingly) if the item isn't passthrough
    const choiceInteractions = passthrough
      ? null
      : [...xmlDocument.querySelectorAll("choiceInteraction")].map(
          generateChoiceInteractions({ accessibilityElements })(xmlDocument),
        );
    const extendedTextInteractions = passthrough
      ? null
      : [...xmlDocument.querySelectorAll("extendedTextInteraction")].map(
          generateExtendedTextInteractions(xmlDocument),
        );

    let format;
    if (passthrough) {
      format = "TEI";
    } else if (choiceInteractions.length) {
      format = "Selected Response";
    } else {
      format = "Constructed Response";
    }

    const content = _.pipe([
      cleanContent({ accessibilityElements }),
      removeClassMsoNormalAttr,
    ])(xmlDocument.querySelector("itemBody")?.innerHTML);

    const cbtCharacterCount = getCharacterCount("cbt")({
      choiceInteractions,
      content,
    });
    const pbtCharacterCount = getCharacterCount("pbt")({
      choiceInteractions,
      content,
    });

    return removeTypenameFromObject({
      ...item,
      accessibilityElements,
      cbtCharacterCount,
      choiceInteractions,
      content,
      extendedTextInteractions,
      format,
      id,
      imageAssets,
      passthrough,
      pbtCharacterCount,
      snapToHighlight,
    });
  };

const generatePassages =
  ({ createdObjects, fileNames, fileTimestamp, items }) =>
  (passage) => {
    const id =
      passage.id ||
      createdObjects.passagesCreated.find((o) => o.name === passage.name)?.id;
    const xmlDocument = getXMLContent(passage.content);

    // Get image assets before removing interaction elements
    const imageAssets = [...xmlDocument.querySelectorAll("img")].map(
      generateImageAssets({ fileNames, fileTimestamp, id }),
    );

    // Generate accessibility elements
    const itemDocument = getXMLContent(
      items.find((item) => passage.items.includes(item.name))?.qtiXml,
    );
    const accessibilityElements =
      generateAccessibilityElements(itemDocument)(xmlDocument);

    const itemIDs = items
      .filter((item) => passage.items.includes(item.name))
      .map((item) => item.id);

    const content = cleanContent({ accessibilityElements })(
      xmlDocument.documentElement.outerHTML,
    );
    const cbtCharacterCount = getCharacterCount("cbt")({
      content,
    });
    const pbtCharacterCount = getCharacterCount("pbt")({
      content,
    });

    return removeTypenameFromObject({
      ...passage,
      accessibilityElements,
      cbtCharacterCount,
      content,
      id,
      imageAssets,
      items: itemIDs,
      pbtCharacterCount,
    });
  };

const generatePresignedS3URLs = (client) => async (s3URLRequests) => {
  return client
    .query(
      getPresignedS3URLsQuery,
      {
        data: s3URLRequests,
      },
      { requestPolicy: "network-only" },
    )
    .toPromise()
    .then((response) => {
      if (response.error) throw new Error(response.error);
      return response.data?.getPresignedS3URLs;
    });
};

const generatePronunciationFeatures = (identifier) => (accessElement) =>
  [...accessElement.getElementsByTagName("apip:textToSpeechPronunciation")]
    .map((element) => {
      const text = element.textContent.trim();
      if (!text) return null;
      return {
        feature: "Text to Speech",
        src: ["mp3", "ogg", "xml"].map((ext) => `${identifier}.${ext}`),
        type: "Spoken",
        text,
      };
    })
    .filter(Boolean);

const generateSpokenFeatures = (accessElement) =>
  [...accessElement.getElementsByTagName("apip:spokenText")]
    .map((element) => {
      const text = element.textContent.trim();
      if (!text) return null;
      return {
        feature: "Audio Text",
        type: "Spoken",
        text,
      };
    })
    .filter(Boolean);

const generateTranslationFeatures = (accessElement) =>
  [...accessElement.getElementsByTagName("apip:keyWordTranslation textString")]
    .map((element) => {
      const text = element.textContent.trim();
      if (!text) return null;
      return {
        type: "Translation",
        text,
      };
    })
    .filter(Boolean);

const getContentStatuses =
  ({ client, program }) =>
  async ({ items, passages }) => {
    const itemNames = items.map(({ name }) => name);
    const passageNames = passages.map(({ name }) => name);
    const [
      existingItemsInProgram,
      existingPassagesInProgram,
      allItems,
      allPassages,
    ] = await Promise.all([
      getItemsByName(client)({ itemList: itemNames, program }),
      getPassagesByName(client)({ passageList: passageNames, program }),
      doItemNamesExist(client)(itemNames),
      doPassageNamesExist(client)(passageNames),
    ]);

    const itemStatuses = items.map(
      validateObject("Item")(existingItemsInProgram)(allItems),
    );
    const passageStatuses = passages.map(
      validateObject("Passage")(existingPassagesInProgram)(allPassages),
    );

    return [...itemStatuses, ...passageStatuses];
  };

const getFileContent = (zipContents) => async (fileName) => {
  const content = await zipContents?.[fileName].async("string");
  return content;
};

const getXMLContent = (file) => {
  const parser = new DOMParser();
  return parser.parseFromString(file, "text/xml");
};

const getImageFilename = (fileNames) => (src) => {
  const filename = src?.split("/").at(-1);

  // If we didn't get a filename from the src, return null
  if (!filename) return null;

  // If the filename doesn't end with .eps, return it
  if (!filename.endsWith(".eps")) return filename;

  // If the filename ends with .eps, find the CBT filename
  const re = new RegExp(
    `${filename.split(".").slice(0, -1).join(".")}(?!\\.eps)`,
    "i",
  );
  return fileNames
    .find((s) => re.test(s))
    ?.split("/")
    .at(-1);
};

const getItemInformation = (files) =>
  _.pipe([
    getXMLContent,
    (content) => [...content.querySelectorAll("resource")],
    _.filter(
      (element) => element.getAttribute("type") === "imsqti_item_xmlv2p1",
    ),
    _.map((element) => ({
      fileContents: files[element.getAttribute("href")],
      name: element.getAttribute("identifier"),
    })),
  ]);

const getItemsFromValidationResults =
  ({ contentArea, contentSpecialist, developmentState, itemWriter, program }) =>
  (validationResults) =>
    validationResults
      .filter(({ type }) => type === "Item")
      .map(({ fileContents, name, object = {}, status, type }) => {
        const { _actions, __typename, ...item } = object;
        return withOptionalMetadata({
          contentArea,
          contentSpecialist,
          developmentState,
          itemWriter,
        })({
          ...item,
          name,
          program,
          qtiXml: fileContents,
          relationships: [],
        });
      });

const getPassageInformation = (files) => {
  return _.pipe([
    _.map(getXMLContent),
    _.map((content) => [...content.getElementsByTagName("xi:include")]),
    _.flatten,
    _.compact,
    _.map((element) => element.getAttribute("href")),
    _.uniq,
    _.map((filename) => ({
      fileContents: files[filename],
      name: filename,
    })),
  ])(Object.values(files));
};

const getPassagesFromValidationResults =
  ({
    contentArea,
    contentSpecialist,
    developmentState,
    itemWriter,
    items,
    program,
  }) =>
  (validationResults) =>
    validationResults
      .filter(({ type }) => type === "Passage")
      .map(({ fileContents, name, object = {}, status, type }) => {
        const { _actions, __typename, ...passage } = object;
        return withOptionalMetadata({
          contentArea,
          contentSpecialist,
          developmentState,
          itemWriter,
        })({
          ...passage,
          content: fileContents,
          items: items
            .filter(({ qtiXml }) =>
              [
                ...getXMLContent(qtiXml).getElementsByTagName("xi:include"),
              ].some((element) => element.getAttribute("href") === name),
            )
            .map((item) => item.name),
          name,
          program,
          relationships: [],
        });
      });

const getPrintableAsset =
  ({ fileNames, fileTimestamp }) =>
  (filename) => {
    // Find a filename with the same base, but with an EPS extension
    const re = new RegExp(
      `${filename.split(".").slice(0, -1).join(".")}(?=\\.eps)`,
      "i",
    );
    const name = fileNames
      .find((s) => re.test(s))
      ?.split("/")
      .at(-1);
    return name ? { name, fileTimestamp } : null;
  };

const getReferencedFiles = _.pipe([
  getXMLContent,
  (content) => [...content.querySelectorAll("file")],
  _.map((element) => element.getAttribute("href")),
]);

const importContent =
  (client) =>
  async ({ batchId, fileName, items, passages, program, results }) => {
    return client
      .mutation(
        getImportContentMutation,
        {
          batchId,
          fileName,
          items,
          passages,
          program,
          results,
        },
        { requestPolicy: "network-only" },
      )
      .toPromise()
      .then((response) => {
        if (response.error) throw new Error(response.error);
        return response.data?.importContent;
      });
  };

const importImageAssets =
  (client) =>
  ({ fileNames, zipContents }) =>
  async (objects) => {
    // Collect all images attached to items/passages
    const imageNames = [
      ...new Set([
        ...objects
          .map(({ imageAssets }) => imageAssets?.map(({ name }) => name) || [])
          .flat(),
      ]),
    ];

    if (!imageNames.length) return [];

    // Generate presigned URLs to upload the images to S3
    const presignedS3URLs = await generatePresignedS3URLs(client)(
      imageNames.map((name) => {
        const ext = name.split(".").at(-1);
        return {
          name,
          fileType: MIME_TYPES[ext],
          addTimestampToName: false,
        };
      }),
    );

    // Collect promises for performing the actual upload
    const imageUploadPromises = presignedS3URLs.map((presignedS3URL) => {
      const fileName = fileNames.find((s) => s.includes(presignedS3URL.name));
      if (!fileName) return Promise.resolve();

      const ext = presignedS3URL.name.split(".").at(-1);
      return zipContents[fileName].async("uint8array").then((body) =>
        fetch(presignedS3URL.url, {
          body,
          headers: new Headers({ "Content-Type": MIME_TYPES[ext] }),
          method: "PUT",
        }),
      );
    });

    return Promise.all(imageUploadPromises);
  };

const buildResultsByKey = (validationResults) =>
  new Map(
    validationResults.map((result) => [
      `${result.type}:${result.name}`,
      {
        details: result.details,
        name: result.name,
        status: result.status,
        type: result.type,
      },
    ]),
  );

// Chunks one kind of content (items or passages) into CHUNKSIZE mutations,
// carrying the validation result for each object along with its own chunk.
// `payloadKey` selects which mutation argument the chunk is sent as.
const buildChunkedImportPromises =
  (client) =>
  ({ batchId, fileName, program, resultFor }) =>
  ({ objects, payloadKey, type }) =>
    _.zip(
      _.chunk(CHUNKSIZE)([...objects]),
      _.chunk(CHUNKSIZE)(objects.map(resultFor(type))),
    ).map(([chunk, results]) =>
      importContent(client)({
        batchId,
        fileName,
        items: [],
        passages: [],
        [payloadKey]: chunk,
        program,
        // Chunked in lockstep with the content, so dropping unmatched entries
        // here cannot shift a result onto the wrong chunk.
        results: _.compact(results),
      }),
    );

const buildImportPromises =
  (client) =>
  ({ batchId, fileName, program, validationResults }) =>
  ({ itemsToImport, passagesToImport }) => {
    const resultsByKey = buildResultsByKey(validationResults);
    const resultFor =
      (type) =>
      ({ name }) =>
        resultsByKey.get(`${type}:${name}`);

    const buildPromises = buildChunkedImportPromises(client)({
      batchId,
      fileName,
      program,
      resultFor,
    });

    return [
      ...buildPromises({
        objects: itemsToImport,
        payloadKey: "items",
        type: "Item",
      }),
      ...buildPromises({
        objects: passagesToImport,
        payloadKey: "passages",
        type: "Passage",
      }),
    ];
  };

const importZip =
  (client) =>
  (program) =>
  ({ contentArea, contentSpecialist, developmentState, itemWriter }) =>
  ({ file, validationResults }) =>
  async (zipContents) => {
    try {
      if (!file?.name) throw new Error("Invalid file");
      if (validationResults.some(({ status }) => status === "Invalid"))
        throw new Error(
          "There are invalid items and/or passages present in the package",
        );

      // Parse the validation results to get initial item/passage data
      const items = getItemsFromValidationResults({
        contentArea,
        contentSpecialist,
        developmentState,
        itemWriter,
        program,
      })(validationResults);
      const passages = getPassagesFromValidationResults({
        contentArea,
        contentSpecialist,
        developmentState,
        itemWriter,
        items,
        program,
      })(validationResults);

      // Create shells for items/passages that do not yet exist in the system
      const createdObjects = await createContentForImport(client)({
        contentArea,
        contentSpecialist,
        developmentState,
        itemWriter,
        items,
        passages,
        program,
      });

      // Parse the file contents and update items/passages accordingly
      const fileNames = Object.keys(zipContents);
      const fileTimestamp = new Date().toISOString();
      const itemsToImport = items.map(
        generateItems({ createdObjects, fileNames, fileTimestamp }),
      );
      const passagesToImport = passages.map(
        generatePassages({
          createdObjects,
          fileNames,
          fileTimestamp,
          items: itemsToImport,
        }),
      );

      // Upload any image assets associated with the items/passages
      await importImageAssets(client)({ fileNames, zipContents })([
        ...itemsToImport,
        ...passagesToImport,
      ]);

      const batchId = uuidv4();

      // Perform the actual import
      const importPromises = buildImportPromises(client)({
        batchId,
        fileName: file.name,
        program,
        validationResults,
      })({ itemsToImport, passagesToImport });
      const responses = await Promise.allSettled(importPromises);
      const importResult = responses
        .filter(({ status }) => status === "fulfilled")
        .map(({ value }) => value)
        .flat(Infinity);

      console.info("The result of the import was: ", importResult);
    } catch (err) {
      console.error(err);
      throw new Error(err);
    }
  };

const validateObject =
  (type) => (existingObjectsInProgram) => (allObjects) => (obj) => {
    const existingObject = existingObjectsInProgram.find(
      (o) => o.name === obj.name,
    );
    if (existingObject) {
      const status = "Valid - Overwrite";
      return {
        ...obj,
        object: existingObject,
        status,
        type,
        details: getImportResultDetails(status),
      };
    }
    const { found } = allObjects.find(({ name }) => name === obj.name);
    const status = found ? "Invalid" : "Valid";
    return {
      ...obj,
      status,
      type,
      details: getImportResultDetails(status),
    };
  };

const validateZip = (client) => (program) => async (file) => {
  try {
    const zipContents = await extractFiles(file);
    if (!zipContents[MANIFEST_FILE_NAME])
      throw new Error(MISSING_MANIFEST_MESSAGE);
    const manifest = await getFileContent(zipContents)(MANIFEST_FILE_NAME);

    const extractedFileNames = Object.keys(zipContents);
    const fileNames = getReferencedFiles(manifest);
    if (!fileNames.every((filename) => extractedFileNames.includes(filename)))
      throw new Error(
        "There are file names in the manifest that are not included in the .zip file",
      );

    const promises = fileNames.map(getFileContent(zipContents));
    const fileContents = await Promise.all(promises);

    const files = fileNames.reduce(
      (acc, filename, index) => ({
        ...acc,
        [filename]: fileContents.at(index),
      }),
      {},
    );
    const items = getItemInformation(files)(manifest);
    if (!items.length) throw new Error("No items found in the manifest file");

    const passages = await getPassageInformation(files);

    const results = await getContentStatuses({ client, program })({
      items,
      passages,
    });

    return [results, zipContents];
  } catch (err) {
    console.error(err);
    throw new Error(err);
  }
};

const getCreateContentForImportMutation = `
  mutation createContentForImport(
    $items: [ItemInput!],
    $passages: [PassageInput!],
    $program: ID!
  ) {
    createContentForImport(
      items: $items,
      passages: $passages,
      program: $program
    ) {
      itemsCreated {
        contentSpecialist {
          id
        }
        developmentState
        id
        itemWriter {
          id
        }
        name
        program
      }
      passagesCreated {
        contentSpecialist {
          id
        }
        developmentState
        id
        itemWriter {
          id
        }
        name
        program
      }
    }
  }
`;

const getImportContentMutation = `
  mutation importContent($batchId: String, $fileName: String!, $items: [ItemInput!], $passages: [PassageInput!], $program: ID!, $results: [ImportResultInput!]) {
    importContent(batchId: $batchId, fileName: $fileName, items: $items, passages: $passages, program: $program, results: $results) {
      id
      itemCount
      passageCount
    }
  }
`;

const groupImportEventsByBatch = (importEvents) =>
  _.pipe([
    _.groupBy((event) => event.batchId ?? event.id),
    _.map((events) => ({
      ...events[0],
      itemCount: _.sumBy("itemCount", events),
      passageCount: _.sumBy("passageCount", events),
      results: _.flatMap((event) => event.results ?? [])(events),
    })),
  ])(importEvents);

const getPresignedS3URLsQuery = `
  query getPresignedS3URLs($data: [PresignedS3URLRequest!]) {
    getPresignedS3URLs(data: $data) {
      name
      url
    }
  }
`;

export {
  buildImportPromises,
  buildResultsByKey,
  getCreateContentForImportMutation,
  getImportContentMutation,
  getImportResultDetails,
  groupImportEventsByBatch,
  importZip,
  MISSING_MANIFEST_MESSAGE,
  validateObject,
  validateZip,
};
